// Editor state: settings per photo, with per-photo undo/redo of immutable snapshots (§10, cap 200).
// A slider drag updates `settings` live (`commit: false`) and pushes ONE history entry on release.
import { create } from 'zustand';
import { defaultSettings, LIMITS } from '../model/defaults';
import type { Settings } from '../model/types';

type History = { past: Settings[]; future: Settings[]; pending: Settings | null };

export type EditorState = {
  settings: Record<string, Settings>;
  history: Record<string, History>;
  /** Bumped on every change, so listeners (sidecar writer, renderer) can subscribe cheaply. */
  version: number;
  get: (id: string) => Settings;
  /** Applies `fn` to a copy of the photo's settings. `commit: false` = live preview during a drag. */
  update: (id: string, fn: (s: Settings) => void, opts?: { commit?: boolean }) => void;
  /** Ends a drag: pushes the pre-drag snapshot as one undo step. */
  commit: (id: string) => void;
  replace: (id: string, s: Settings, opts?: { history?: boolean }) => void;
  undo: (id: string) => void;
  redo: (id: string) => void;
  /**
   * Applies `fn` to many photos in ONE store update (one undo step per photo). Returns the previous
   * settings so a global "Undo paste to N photos" can restore them all (§10).
   */
  updateMany: (ids: string[], fn: (s: Settings, id: string) => Settings | void) => Record<string, Settings>;
  /** Restores several photos at once (the global batch undo); records one undo step per photo. */
  restoreMany: (before: Record<string, Settings>) => void;
  canUndo: (id: string) => boolean;
  canRedo: (id: string) => boolean;
};

const clone = (s: Settings): Settings => structuredClone(s);
const emptyHistory = (): History => ({ past: [], future: [], pending: null });

export const useEditor = create<EditorState>((set, get) => ({
  settings: {},
  history: {},
  version: 0,
  get: (id) => get().settings[id] ?? defaultSettings(),
  update: (id, fn, opts) => {
    const commit = opts?.commit ?? true;
    set((st) => {
      const cur = st.settings[id] ?? defaultSettings();
      const next = clone(cur);
      fn(next);
      const h = st.history[id] ?? emptyHistory();
      let nh: History;
      if (commit) {
        const before = h.pending ?? cur;
        nh = { past: [...h.past, before].slice(-LIMITS.history), future: [], pending: null };
      } else nh = { ...h, pending: h.pending ?? cur };
      return {
        settings: { ...st.settings, [id]: next },
        history: { ...st.history, [id]: nh },
        version: st.version + 1,
      };
    });
  },
  commit: (id) => {
    set((st) => {
      const h = st.history[id];
      if (!h?.pending) return st;
      return {
        history: {
          ...st.history,
          [id]: { past: [...h.past, h.pending].slice(-LIMITS.history), future: [], pending: null },
        },
        version: st.version + 1,
      };
    });
  },
  replace: (id, s, opts) => {
    set((st) => {
      const h = st.history[id] ?? emptyHistory();
      const cur = st.settings[id];
      const nh =
        opts?.history && cur
          ? { past: [...h.past, cur].slice(-LIMITS.history), future: [], pending: null }
          : h;
      return {
        settings: { ...st.settings, [id]: clone(s) },
        history: { ...st.history, [id]: nh },
        version: st.version + 1,
      };
    });
  },
  undo: (id) => {
    set((st) => {
      const h = st.history[id];
      if (!h || h.past.length === 0) return st;
      const prev = h.past[h.past.length - 1]!;
      const cur = st.settings[id] ?? defaultSettings();
      return {
        settings: { ...st.settings, [id]: prev },
        history: {
          ...st.history,
          [id]: { past: h.past.slice(0, -1), future: [cur, ...h.future], pending: null },
        },
        version: st.version + 1,
      };
    });
  },
  redo: (id) => {
    set((st) => {
      const h = st.history[id];
      if (!h || h.future.length === 0) return st;
      const next = h.future[0]!;
      const cur = st.settings[id] ?? defaultSettings();
      return {
        settings: { ...st.settings, [id]: next },
        history: {
          ...st.history,
          [id]: { past: [...h.past, cur], future: h.future.slice(1), pending: null },
        },
        version: st.version + 1,
      };
    });
  },
  updateMany: (ids, fn) => {
    const before: Record<string, Settings> = {};
    set((st) => {
      const settings = { ...st.settings };
      const history = { ...st.history };
      for (const id of ids) {
        const cur = st.settings[id] ?? defaultSettings();
        before[id] = cur;
        const draft = clone(cur);
        const res = fn(draft, id);
        settings[id] = res ?? draft;
        const h = st.history[id] ?? emptyHistory();
        history[id] = { past: [...h.past, cur].slice(-LIMITS.history), future: [], pending: null };
      }
      return { settings, history, version: st.version + 1 };
    });
    return before;
  },
  restoreMany: (before) => {
    set((st) => {
      const settings = { ...st.settings };
      const history = { ...st.history };
      for (const [id, s] of Object.entries(before)) {
        const cur = st.settings[id] ?? defaultSettings();
        settings[id] = s;
        const h = st.history[id] ?? emptyHistory();
        history[id] = { past: [...h.past, cur].slice(-LIMITS.history), future: [], pending: null };
      }
      return { settings, history, version: st.version + 1 };
    });
  },
  canUndo: (id) => (get().history[id]?.past.length ?? 0) > 0,
  canRedo: (id) => (get().history[id]?.future.length ?? 0) > 0,
}));
